/* ============================================
   GIGLIOTTO TENUTE — Global JS
   Navbar, mobile menu, search, AJAX cart,
   language switcher, smooth scroll
   ============================================ */

document.addEventListener('DOMContentLoaded', () => {
  initNavbar();
  initNavbarTheme();
  initMobileMenu();
  initSearch();
  initAjaxCart();
  initLangSwitcher();
  initSmoothScroll();
});

/* ── Navbar scroll state ── */
function initNavbar() {
  const navbar = document.querySelector('.navbar');
  if (!navbar) return;

  const checkScroll = () => {
    navbar.classList.toggle('scrolled', window.scrollY > 60);
  };

  window.addEventListener('scroll', checkScroll, { passive: true });
  checkScroll();
}

/* ── Navbar theme (logo switching based on section background) ── */
function initNavbarTheme() {
  const navbar = document.querySelector('.navbar');
  if (!navbar) return;

  const sections = document.querySelectorAll('[data-nav-theme]');
  if (!sections.length) return;

  const navHeight = navbar.offsetHeight;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.intersectionRatio >= 0.4) {
        navbar.classList.toggle('navbar--inverted', entry.target.dataset.navTheme === 'light');
      }
    });
  }, {
    threshold: [0.4],
    rootMargin: `-${navHeight}px 0px -${window.innerHeight - navHeight * 2}px 0px`
  });

  sections.forEach(s => observer.observe(s));
}

/* ── Mobile menu ── */
function initMobileMenu() {
  const hamburger = document.querySelector('.navbar__hamburger');
  const nav       = document.querySelector('.navbar__nav');
  const overlay   = document.querySelector('.nav-overlay');
  if (!hamburger || !nav) return;

  const toggle = () => {
    hamburger.classList.toggle('active');
    nav.classList.toggle('open');
    overlay?.classList.toggle('active');
    document.body.style.overflow = nav.classList.contains('open') ? 'hidden' : '';
  };

  hamburger.addEventListener('click', toggle);
  overlay?.addEventListener('click', toggle);
}

/* ── Search drawer ── */
function initSearch() {
  const toggle   = document.getElementById('search-toggle');
  const drawer   = document.getElementById('search-drawer');
  const overlay  = document.getElementById('search-overlay');
  const closeBtn = document.getElementById('search-close');
  const input    = document.getElementById('search-input');
  const results  = document.getElementById('search-results');
  if (!toggle || !drawer) return;

  const open = () => {
    drawer.classList.add('open');
    overlay?.classList.add('active');
    toggle.setAttribute('aria-expanded', 'true');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    setTimeout(() => input?.focus(), 50);
  };

  const close = () => {
    drawer.classList.remove('open');
    overlay?.classList.remove('active');
    toggle.setAttribute('aria-expanded', 'false');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    if (results) results.innerHTML = '';
    if (input)   input.value = '';
  };

  toggle.addEventListener('click', open);
  closeBtn?.addEventListener('click', close);
  overlay?.addEventListener('click', close);
  document.addEventListener('keydown', e => { if (e.key === 'Escape') close(); });

  let debounceTimer;
  input?.addEventListener('input', () => {
    clearTimeout(debounceTimer);
    const q = input.value.trim();
    if (q.length < 2) { if (results) results.innerHTML = ''; return; }
    debounceTimer = setTimeout(() => fetchPredictive(q, results), 300);
  });
}

async function fetchPredictive(q, container) {
  if (!container) return;
  try {
    const url = `/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6&resources[options][fields]=title,product_type,variants.title`;
    const res  = await fetch(url);
    const data = await res.json();
    renderPredictive(data?.resources?.results?.products || [], q, container);
  } catch {
    container.innerHTML = `<div class="search-drawer__no-results">${window.searchStrings?.error || 'Error'}</div>`;
  }
}

function renderPredictive(products, q, container) {
  if (!products.length) {
    container.innerHTML = `<div class="search-drawer__no-results">${window.searchStrings?.no_results?.replace('{{ terms }}', q) || q}</div>`;
    return;
  }

  const cards = products.map(p => {
    const img   = p.featured_image?.url
      ? `<img class="search-result-card__image" src="${p.featured_image.url}&width=112" alt="${p.title}" loading="lazy">`
      : `<div class="search-result-card__image"></div>`;
    const price = p.price_min
      ? `<span class="search-result-card__price">${(p.price_min / 100).toLocaleString(document.documentElement.lang || 'it-IT', { style: 'currency', currency: 'EUR' })}</span>`
      : '';
    return `<a href="${p.url}" class="search-result-card">${img}<div class="search-result-card__info"><div class="search-result-card__name">${p.title}</div>${price}</div></a>`;
  }).join('');

  const viewAll = window.searchStrings?.view_all || 'View all results →';
  container.innerHTML = `<div class="search-drawer__items">${cards}</div><a href="/search?type=product&q=${encodeURIComponent(q)}" class="search-drawer__view-all">${viewAll}</a>`;
}

/* ── AJAX Cart ── */
function initAjaxCart() {
  document.querySelectorAll('form[action$="/cart/add"]').forEach(form => {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const submitBtn = form.querySelector('[type="submit"]');
      if (submitBtn) submitBtn.disabled = true;

      try {
        const res  = await fetch(window.routes.cart_add_url + '.js', { method: 'POST', body: new FormData(form) });
        const data = await res.json();
        updateCartCount();
        showCartToast(data.product_title);
      } catch (err) {
        console.error('Cart Error:', err);
      } finally {
        if (submitBtn) submitBtn.disabled = false;
      }
    });
  });
}

async function updateCartCount() {
  const res   = await fetch(window.routes.cart_url + '.js');
  const cart  = await res.json();
  const badge = document.getElementById('cart-icon-bubble');
  if (!badge) return;
  badge.textContent = cart.item_count;
  badge.style.animation = 'none';
  requestAnimationFrame(() => { badge.style.animation = 'pulse 0.3s'; });
}

function showCartToast(name) {
  const toast = document.getElementById('cart-toast');
  if (!toast) return;
  const text = toast.querySelector('.cart-toast__text');
  if (text) text.innerHTML = `<strong>${name}</strong>`;
  toast.classList.add('active');
  setTimeout(() => toast.classList.remove('active'), 3000);
}

/* ── Language switcher ── */
function initLangSwitcher() {
  const switcher = document.querySelector('.lang-switcher');
  if (!switcher) return;

  const btn      = switcher.querySelector('.lang-switcher__btn');
  const dropdown = switcher.querySelector('.lang-switcher__dropdown');
  if (!btn || !dropdown) return;

  btn.addEventListener('click', e => {
    e.stopPropagation();
    const open = dropdown.classList.toggle('is-open');
    btn.setAttribute('aria-expanded', open ? 'true' : 'false');
  });

  document.addEventListener('click', () => {
    dropdown.classList.remove('is-open');
    btn.setAttribute('aria-expanded', 'false');
  });

  document.addEventListener('keydown', e => {
    if (e.key === 'Escape') {
      dropdown.classList.remove('is-open');
      btn.setAttribute('aria-expanded', 'false');
      btn.focus();
    }
  });
}

/* ── Smooth scroll ── */
function initSmoothScroll() {
  document.querySelectorAll('a[href^="#"]').forEach(anchor => {
    anchor.addEventListener('click', e => {
      const target = document.querySelector(anchor.getAttribute('href'));
      if (target) {
        e.preventDefault();
        target.scrollIntoView({ behavior: 'smooth' });
      }
    });
  });
}
